import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from '../../..';
import Context from '../../../../store/context';
import useFetch from '../../../../services/hooks/http/useFetch';
import apiEndpoints from '../../../../apis';
import classes from './InternationalBankDeposit.module.scss';

const InternationalBankDeposit = ({ onAccept, totalValueToPay, costTransaction, suppliersGrouping, pricingData }) => {
  const {
    toast: { showSuccess, showError }
  } = useContext(Context);
  
  const { userId } = useSelector(state => state.userData);
  const [isNotifying, setIsNotifying] = useState(false);
  const [exchangeRate, setExchangeRate] = useState(null);
  const [usdAmount, setUsdAmount] = useState(null);
  
  const { sendRequest: sendNotification } = useFetch(apiEndpoints.sendInternationalNotification);

  // Usar datos dinámicos del pricing si están disponibles, sino usar defaults
  const bankDetails = pricingData?.INTERNATIONAL_BANK_ACCOUNT || {
    beneficiaryName: 'MELO GLOBAL CORP',
    beneficiaryAddress: '251 Crandon Boulevard, 129, Key Biscayne, Florida 331490000, US',
    routingNumber: '101019644',
    accountNumber: '217678091854',
    accountType: 'Checking',
    bankName: 'Lead Bank',
    bankAddress: '1801 Main St., Kansas City, MO 64108'
  };

  // Usar spread dinámico del pricing si está disponible, sino usar default del 2%
  const EXCHANGE_RATE_SPREAD_PERCENT = pricingData?.INTERNATIONAL_EXCHANGE_RATE_SPREAD_PERCENT || 0.02;

  // El fee no se cobra, solo se muestra tachado como descuento especial
  const totalWithFees = totalValueToPay;
  
  // Obtener tasa de cambio COP a USD
  useEffect(() => {
    const fetchExchangeRate = async () => {
      try {
        // Usar API de exchangerate-api.com (gratuita)
        const response = await fetch('https://api.exchangerate-api.com/v4/latest/COP');
        const data = await response.json();
        const marketRate = data.rates.USD; // Tasa del mercado (COP a USD)
        
        // Aplicar spread del 2% a la TRM
        // 1. Convertir rate a TRM (COP/USD): TRM = 1 / rate
        // 2. Aplicar spread: TRM_con_spread = TRM * (1 + spread)
        // 3. Convertir de vuelta a rate: rate_con_spread = 1 / TRM_con_spread
        const marketTRM = 1 / marketRate; // COP/USD
        const trmWithSpread = marketTRM * (1 + EXCHANGE_RATE_SPREAD_PERCENT);
        const rateWithSpread = 1 / trmWithSpread;
        
        setExchangeRate(rateWithSpread);
        
        // Calcular monto en USD con el spread aplicado
        const convertedUSD = totalWithFees * rateWithSpread;
        setUsdAmount(convertedUSD);
      } catch (error) {
        console.error('Error obteniendo tasa de cambio:', error);
        // Fallback: TRM = 4200, con spread del 2% = 4284
        const fallbackTRM = 4200 * (1 + EXCHANGE_RATE_SPREAD_PERCENT);
        const fallbackRate = 1 / fallbackTRM;
        setExchangeRate(fallbackRate);
        setUsdAmount(totalWithFees * fallbackRate);
      }
    };
    
    fetchExchangeRate();
  }, [totalWithFees, EXCHANGE_RATE_SPREAD_PERCENT]);
  
  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      showSuccess('Copiado!');
    } catch (err) {
      console.error('Error al copiar al portapapeles:', err);
    }
  };

  const handleContinue = async () => {
    try {
      setIsNotifying(true);
      
      // Enviar notificación a los proveedores
      await sendNotification({
        userId,
        suppliersGrouping
      });
      
      showSuccess('Notificación enviada exitosamente a los proveedores');
      
      // Crear un objeto que simule una cuenta de origen para usuarios internacionales
      const internationalAccount = {
        name: 'MELO GLOBAL CORP',
        bankId: 'lead-bank',
        subtypeLabel: 'Checking',
        bankLast4Digits: '5854',
        identification: 'International Wire Transfer',
        routingNumber: bankDetails.routingNumber,
        accountNumber: bankDetails.accountNumber,
        isInternational: true
      };

      onAccept('international', [internationalAccount]);
    } catch (error) {
      console.error('Error enviando notificación:', error);
      showError('Error al enviar la notificación. Por favor, intenta nuevamente.');
    } finally {
      setIsNotifying(false);
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <h2 className={`${classes.title} bold-24`}>Información de Consignación Bancaria</h2>
        <p className={classes.subtitle}>
          Para completar tu pago, realiza una transferencia bancaria a la cuenta de MELO GLOBAL CORP en Estados Unidos.
        </p>
      </div>

      <div className={classes.bankDetails}>
        <div className={classes.field}>
          <label className={classes.label}>Nombre del Beneficiario</label>
          <div className={classes.fieldContainer}>
            <span className={classes.value}>{bankDetails.beneficiaryName}</span>
            <button 
              type="button"
              className={classes.copyButton}
              onClick={() => copyToClipboard(bankDetails.beneficiaryName, 'Nombre del beneficiario')}
              title="Copiar"
            >
              Copiar
            </button>
          </div>
        </div>

        <div className={classes.field}>
          <label className={classes.label}>Dirección del Beneficiario</label>
          <div className={classes.fieldContainer}>
            <span className={classes.value}>{bankDetails.beneficiaryAddress}</span>
            <button 
              type="button"
              className={classes.copyButton}
              onClick={() => copyToClipboard(bankDetails.beneficiaryAddress, 'Dirección del beneficiario')}
              title="Copiar"
            >
              Copiar
            </button>
          </div>
        </div>

        <div className={classes.field}>
          <label className={classes.label}>Número de Ruta ACH y Wire</label>
          <div className={classes.fieldContainer}>
            <span className={classes.value}>{bankDetails.routingNumber}</span>
            <button 
              type="button"
              className={classes.copyButton}
              onClick={() => copyToClipboard(bankDetails.routingNumber, 'Número de ruta')}
              title="Copiar"
            >
              Copiar
            </button>
          </div>
        </div>

        <div className={classes.field}>
          <label className={classes.label}>Número de Cuenta</label>
          <div className={classes.fieldContainer}>
            <span className={classes.value}>{bankDetails.accountNumber}</span>
            <button 
              type="button"
              className={classes.copyButton}
              onClick={() => copyToClipboard(bankDetails.accountNumber, 'Número de cuenta')}
              title="Copiar"
            >
              Copiar
            </button>
          </div>
        </div>

        <div className={classes.field}>
          <label className={classes.label}>Tipo de Cuenta</label>
          <div className={classes.fieldContainer}>
            <span className={classes.value}>{bankDetails.accountType}</span>
            <button 
              type="button"
              className={classes.copyButton}
              onClick={() => copyToClipboard(bankDetails.accountType, 'Tipo de cuenta')}
              title="Copiar"
            >
              Copiar
            </button>
          </div>
        </div>

        <div className={classes.field}>
          <label className={classes.label}>Nombre del Banco</label>
          <div className={classes.fieldContainer}>
            <span className={classes.value}>{bankDetails.bankName}</span>
            <button 
              type="button"
              className={classes.copyButton}
              onClick={() => copyToClipboard(bankDetails.bankName, 'Nombre del banco')}
              title="Copiar"
            >
              Copiar
            </button>
          </div>
        </div>

        <div className={classes.field}>
          <label className={classes.label}>Dirección del Banco</label>
          <div className={classes.fieldContainer}>
            <span className={classes.value}>{bankDetails.bankAddress}</span>
            <button 
              type="button"
              className={classes.copyButton}
              onClick={() => copyToClipboard(bankDetails.bankAddress, 'Dirección del banco')}
              title="Copiar"
            >
              Copiar
            </button>
          </div>
        </div>
      </div>

      <div className={classes.instructions}>
        <h3 className={classes.instructionsTitle}>Instrucciones para la Transferencia:</h3>
        <ul className={classes.instructionsList}>
          <li>Realiza una transferencia bancaria internacional (Wire Transfer) a los datos proporcionados</li>
          <li>
            El monto a transferir es: <strong>
              {usdAmount ? `$${usdAmount.toFixed(2)} USD` : `Calculando...`}
            </strong> (aprox. ${totalWithFees?.toLocaleString()} COP)
            {exchangeRate && <span className={classes.exchangeRate}> | TRM: ${(1/exchangeRate).toFixed(2)} COP/USD</span>}
          </li>
          <li>Asegúrate de incluir tu número de referencia de pago en las notas de la transferencia</li>
          <li>Una vez realizada la transferencia, nuestro equipo verificará el pago y procesará tus facturas</li>
          <li>Recibirás una confirmación por email una vez que el pago sea verificado</li>
        </ul>
      </div>

      <div className={classes.actions}>
        <Button
          type="button"
          className={classes.continueButton}
          custom="primary"
          color="purple"
          onClick={handleContinue}
          disabled={isNotifying}
        >
          {isNotifying ? 'Enviando notificación...' : 'Confirmar Transferencia'}
        </Button>
      </div>
    </div>
  );
};

export default InternationalBankDeposit;
